import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyContainer - The sticky container
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} variantModal - The variant modal
 * @property {HTMLElement} modalOverlay - The modal overlay
 * @property {HTMLElement} modalClose - The modal close button
 * @property {HTMLElement} modalBody - The modal body
 * @property {HTMLButtonElement} modalAddToCart - The modal add to cart button
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyContainer', 'stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isModalOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupModal();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const hasVariants = this.dataset.hasVariants === 'true';
    
    // If product has variants, open the modal instead
    if (hasVariants && this.refs.variantModal && !this.refs.variantModal.hasAttribute('hidden')) {
      this.#openModal();
      return;
    }

    // Otherwise, proceed with normal add to cart
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton) return;
    
    // Use product image from modal or container if available
    const productImage = this.refs.productImage || 
      this.querySelector('.sticky-add-to-cart__image-img');
    
    if (!productImage) {
      // If no image, just reset the button state
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
      return;
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyContainer?.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    if (this.refs.stickyContainer) {
      this.refs.stickyContainer.setAttribute('data-stuck', currentStuck);
    }
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyContainer } = this.refs;
    this.#isStuck = true;
    stickyContainer.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyContainer } = this.refs;
    this.#isStuck = false;
    stickyContainer.dataset.stuck = 'false';
  }

  /**
   * Sets up the variant modal
   */
  #setupModal() {
    if (!this.refs.variantModal) return;

    const { signal } = this.#abortController;
    const { modalOverlay, modalClose, modalAddToCart } = this.refs;

    // Close modal handlers
    const closeModal = (e) => {
      e?.stopPropagation();
      this.#closeModal();
    };
    
    if (modalOverlay) {
      modalOverlay.addEventListener('click', closeModal, { signal });
    }
    
    if (modalClose) {
      modalClose.addEventListener('click', closeModal, { signal });
    }

    // Handle escape key - only if this modal is open
    const handleEscape = (e) => {
      if (e.key === 'Escape' && this.#isModalOpen && this.refs.variantModal?.dataset.open === 'true') {
        e.stopPropagation();
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape, { signal });

    // Handle add to cart from modal
    if (modalAddToCart) {
      modalAddToCart.addEventListener('click', this.#handleModalAddToCart, { signal });
    }

    // Listen for variant changes in modal to update the add to cart button and sync with main picker
    const modalVariantPicker = this.refs.variantModal?.querySelector('variant-picker');
    if (modalVariantPicker) {
      modalVariantPicker.addEventListener(ThemeEvents.variantSelected, (event) => {
        // Sync the selection with the main variant picker on the page
        const productId = this.dataset.productId;
        const mainVariantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]:not(.sticky-variant-modal variant-picker)`);
        
        if (mainVariantPicker && event.detail.resource) {
          const selectedOptionId = event.detail.resource.id;
          if (selectedOptionId) {
            // Find the corresponding option in the main variant picker and select it
            const mainOption = mainVariantPicker.querySelector(`[data-option-value-id="${selectedOptionId}"]`);
            if (mainOption) {
              mainVariantPicker.updateSelectedOption(mainOption);
            }
          }
        }
      }, { signal });

      modalVariantPicker.addEventListener(ThemeEvents.variantUpdate, (event) => {
        const variant = event.detail.resource;
        if (modalAddToCart) {
          modalAddToCart.disabled = !variant || !variant.available;
        }
        // Update the current variant ID
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
          this.dataset.variantAvailable = variant.available ? 'true' : 'false';
        }
      }, { signal });
    }
  }

  /**
   * Opens the variant modal
   */
  #openModal() {
    if (!this.refs.variantModal) return;
    this.#isModalOpen = true;
    this.refs.variantModal.dataset.open = 'true';
    // Use the theme's scroll lock mechanism instead of directly manipulating body
    document.documentElement.setAttribute('scroll-lock', '');
  }

  /**
   * Closes the variant modal
   */
  #closeModal() {
    if (!this.refs.variantModal) return;
    this.#isModalOpen = false;
    this.refs.variantModal.dataset.open = 'false';
    // Only remove scroll lock if no other modals/drawers are open
    // Check for other modals (excluding this one)
    const otherModals = Array.from(document.querySelectorAll('[data-open="true"]')).filter(
      el => !el.classList.contains('sticky-variant-modal') || el !== this.refs.variantModal
    );
    const hasDrawers = document.querySelector('.drawer[data-open="true"]');
    if (otherModals.length === 0 && !hasDrawers) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /**
   * Handles add to cart from the modal
   */
  #handleModalAddToCart = async () => {
    // Get the selected variant from the modal's variant picker
    const modalVariantPicker = this.refs.variantModal?.querySelector('variant-picker');
    if (!modalVariantPicker) return;

    const selectedOption = modalVariantPicker.selectedOption;
    if (!selectedOption) return;

    const variantId = selectedOption.dataset.variantId;
    if (!variantId) return;

    // Close the modal first
    this.#closeModal();

    // Wait a bit for the modal to close and variant to update, then trigger add to cart
    setTimeout(() => {
      if (!this.#targetAddToCartButton) return;
      this.#targetAddToCartButton.dataset.puppet = 'true';
      this.#targetAddToCartButton.click();
      
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      if (!cartIcon) return;

      // Try to get product image
      const sectionElement = this.closest('.shopify-section');
      const sectionId = sectionElement?.id.replace('shopify-section-', '');
      const productImage = this.querySelector('.sticky-add-to-cart__image-img') ||
        document.querySelector(`#shopify-section-${sectionId} [ref="productImage"]`) ||
        document.querySelector(`#shopify-section-${sectionId} .product-media-gallery img`);

      if (productImage && this.refs.addToCartButton) {
        if (this.refs.addToCartButton.dataset.added !== 'true') {
          this.refs.addToCartButton.dataset.added = 'true';
        }

        if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

        const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
        flyToCartElement.classList.add('fly-to-cart--sticky');
        flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
        flyToCartElement.useSourceSize = 'true';
        flyToCartElement.source = productImage;
        flyToCartElement.destination = cartIcon;

        document.body.appendChild(flyToCartElement);

        onAnimationEnd([this.refs.addToCartButton, flyToCartElement]).then(() => {
          this.#resetTimeout = setTimeout(() => {
            this.refs.addToCartButton.removeAttribute('data-added');
          }, 800);
        });
      }
    }, 400);
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
